/* ------------------------------------------------------------------------
 * The prior-acts anchor, and the extended reporting period, as CONCEPTS
 * rather than as the words one carrier happened to print.
 *
 * Why this module exists
 * ---------------------
 * Aspen View's cyber policy is Beazley Breach Response 5.0. Its declarations
 * carry:
 *
 *     Continuity Date:                    01-Jul-2020
 *
 * The report said "Retro Date Not Found". That is the worst kind of wrong: a
 * stated term reported as absent, which reads to a broker as an exposure to
 * go and fix. It failed for THREE independent reasons, and a longer synonym
 * list only repairs the first:
 *
 *   1. LABEL.  The scan matched the literal token /\bretroactive\b/. Beazley
 *      says "Continuity Date". Chubb and AIG management liability say the
 *      same. Lloyd's paper says "Prior Acts Date".
 *
 *   2. VALUE.  The date grammar was m/d/y and "August 24, 2025" only. It
 *      cannot read 01-Jul-2020, and London and Bermuda paper is dd-Mon-yyyy
 *      almost without exception. So even after the label matched, the value
 *      would have come back empty and the row would still have read
 *      "Not Found".
 *
 *   3. CONCEPT.  Widening the vocabulary naively is how you get the OTHER
 *      failure — promoting a date that looks like a retro anchor and is not
 *      one. A "Prior and Pending Litigation Date" and a "Knowledge Date" sit
 *      in the same declarations item block, in the same dd-Mon-yyyy format,
 *      and mean something else entirely. Reporting one as the retro date is
 *      the same defect class as the $250 life benefit lifted off a dental
 *      table (issue #16): right page, wrong block, stated with a citation.
 *
 * The fourth thing, which is not a parser at all: we report the carrier's
 * OWN label. The row says "Continuity Date — 01-Jul-2020", not "Retroactive
 * Date — 01-Jul-2020". That way SentriPol does not have to be silently right
 * about a synonym. If the equivalence is wrong, a broker sees the policy's
 * word and catches it in one glance, which is the whole grounding rule: say
 * what the document says, cite where, and let the reader check.
 * ------------------------------------------------------------------------ */

(function () {
  'use strict';
  const root = (typeof window !== 'undefined') ? window : globalThis;

  // -----------------------------------------------------------------------
  // 0. SHAPE — one printed row per line, columns separated by whitespace.
  //
  // These readers are line-based, and the text they receive is not.
  //
  // The browser does not parse PDFs locally: extractPdfText goes to the
  // BACKEND first for every PDF, and the backend runs LlamaParse, which
  // returns MARKDOWN. A forms schedule arrives as a markdown table:
  //
  //     | CG 00 01    | 04 13        | Commercial General Liability Coverage Form |
  //
  // and some tables arrive as HTML instead. Every pattern here expects the
  // columns separated by whitespace, so a pipe between the form number and
  // its edition date defeated the forms-schedule reader completely — which is
  // why General Liability kept reporting Claims-Made in the browser while the
  // same policy read correctly from a layout-preserving extractor.
  //
  // Normalising the table furniture away gives one shape that both LlamaParse
  // markdown and a reconstructed PDF.js page reduce to. It runs on a COPY:
  // the EB plan-table readers need the markdown grid intact and read the
  // original text.
  // -----------------------------------------------------------------------
  /* A TABLE READ AS A TABLE.
   *
   * spTableLines flattens a grid into one line per printed row, and every
   * reader downstream then reconstructs the cells from whitespace. That
   * round trip loses what the grid knew, and the loss is measurable: on one
   * four-policy program twelve amounts are read TWICE with overlapping
   * labels, and the difference between the two labels is the qualifier —
   *
   *     $26,582,592   "Building"  and  "Building per Occurrence"
   *     $1,000,000    "Abuse Or Molestation Liability"
   *                   and "Abuse Or Molestation Liability Each Act Limit"
   *
   * — four labels are sentence fragments left by a wrapped cell ("The lesser
   * of 14 days or"), and 54 of 138 amounts carry no coverage scope at all,
   * because a schedule page has a caption rather than a heading.
   *
   * A grid keeps the three facts a limit needs separate: which row it is on,
   * which column it is under, and what the table is about. Returns
   *   [{ caption, header: [cells], rows: [[cells]], line }]
   * with no interpretation applied — that belongs to the caller.
   */
  function spTableGrid(text) {
    const src = String(text || '');
    if (!src) return [];
    // HTML tables are turned into markdown rows first, so one parser serves
    // both shapes and neither drifts from the other.
    let t = src;
    if (t.indexOf('<') >= 0 && /<t[dhr][\s>]/i.test(t)) {
      t = t.replace(/<tr[^>]*>([\s\S]*?)<\/tr>/gi, (_m, inner) => {
        const cells = [];
        inner.replace(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi, (_x, c) => {
          cells.push(c.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim());
          return '';
        });
        return cells.length ? '\n| ' + cells.join(' | ') + ' |\n' : '\n';
      }).replace(/<[^>]+>/g, ' ');
    }
    t = t.replace(/<\/?(?:u|b|i|em|strong|span|small|sup|sub|br)\s*\/?>/gi, '');
    const lines = t.split('\n');
    const cellsOf = l => l.replace(/^\s*\|/, '').replace(/\|\s*$/, '')
                          .split('|')
                          .map(c => c.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\s+/g, ' ').trim());
    const isRow = l => /^\s*\|/.test(l) && l.indexOf('|', l.indexOf('|') + 1) >= 0;
    const isRule = l => /^[\s|:\-]+$/.test(l) && l.indexOf('-') >= 0;
    const out = [];
    let i = 0;
    while (i < lines.length) {
      if (!isRow(lines[i]) || isRule(lines[i])) { i++; continue; }
      const start = i;
      const block = [];
      while (i < lines.length && (isRow(lines[i]) || isRule(lines[i]))) {
        if (!isRule(lines[i])) block.push(cellsOf(lines[i]));
        i++;
      }
      if (!block.length) continue;
      // A header row is one the source marked with a rule beneath it. Where
      // there is none, the table has no header and every row is data — saying
      // so beats promoting a data row and losing it.
      const ruled = lines[start + 1] !== undefined && isRule(lines[start + 1]);
      const header = ruled ? block[0] : [];
      const rows = ruled ? block.slice(1) : block;
      // The caption is the nearest preceding line with words on it. On a
      // schedule page that is the only thing naming the coverage.
      let caption = '';
      for (let k = start - 1; k >= 0 && k > start - 6; k--) {
        const c = String(lines[k] || '').replace(/^\s{0,3}#{1,6}\s*/, '')
                   .replace(/\*\*(.+?)\*\*/g, '$1').trim();
        if (!c) continue;
        if (isRow(c) || isRule(c)) break;
        caption = c;
        break;
      }
      out.push({ caption, header, rows, line: start });
    }
    return out;
  }

  function spTableLines(text) {
    let t = String(text || '');
    if (t.indexOf('<') >= 0 && /<t[dhr][\s>]/i.test(t)) {
      // An HTML table ROW is one printed row, and LlamaParse emits each cell
      // on its own source line:
      //
      //     <tr>
      //       <td>Blanket Type: Building and/or Personal Property Combin</td>
      //     <td>$ 2,068,700</td>
      //     </tr>
      //
      // Stripping the tags alone leaves the label and its amount on separate
      // lines, so a reader that binds a value to its label's line finds
      // nothing. That is why Blanket Group 002 came back empty and the
      // personal property limit vanished from the property row, while Group
      // 001 — which LlamaParse happened to render as a MARKDOWN table — read
      // fine. Collapse each row to a single line first.
      /* The cell boundary is marked before the tags go, and restored after
       * the whitespace collapse — see the markdown branch below for why
       * collapsing first destroys it. */
      const CELL = '\u0000';
      t = t.replace(/<tr[^>]*>([\s\S]*?)<\/tr>/gi, (_m, inner) =>
            '\n' + inner.replace(/<\/t[dh]>/gi, CELL)
                        .replace(/<[^>]+>/g, ' ')
                        .replace(/\s*\n\s*/g, ' ')
                        .replace(/[ \t]+/g, ' ')
                        .split(CELL).map(c => c.trim()).join('   ').trim() + '\n')
           .replace(/<\/t[dh]>/gi, '   ')
           .replace(/<[^>]+>/g, ' ');
    }
    // Inline formatting tags, which LlamaParse uses freely and which no reader
    // here wants. "<u>**Policy Period:**</u>" put "**</u>" between the label
    // and the date on the next line, so a pattern expecting whitespace after
    // the colon matched nothing and the Package and Auto policies dropped out
    // of the program renewal table entirely. Only inline tags are touched;
    // table tags are handled above and everything else is left alone.
    t = t.replace(/<\/?(?:u|b|i|em|strong|span|small|sup|sub|br)\s*\/?>/gi, '');
    return t.split('\n').map(line => {
      // Markdown decoration. A coverage block announces itself as
      // "### **Employee Benefits Liability Coverage**", and every pattern that
      // looks for a heading expects the words alone — so the 07/01/2014 retro
      // date underneath it was attributed to the coverage ABOVE, which is the
      // very misattribution this attribution was written to stop.
      let l = line.replace(/^\s{0,3}#{1,6}\s*/, '')
                  .replace(/\*\*(.+?)\*\*/g, '$1')
                  .replace(/__(.+?)__/g, '$1');
      if (l.indexOf('|') < 0) return l;
      // A markdown separator row ("|---|:--|") carries no content.
      if (/^[\s|:\-]+$/.test(l)) return '';
      /* THE CELL BOUNDARY HAS TO SURVIVE THE WHITESPACE COLLAPSE.
       *
       * These two lines used to run in this order: replace each pipe with
       * three spaces, then collapse every run of whitespace to one. The
       * second undid the first, so a flattened table row reached the readers
       * with no cell boundaries at all — and spReadFields splits cells on two
       * or more spaces, which is the contract this function exists to honour.
       *
       * A two-column row survived anyway, because its amount ends the line
       * and a separate trailing-money rule catches it. Anything with a
       * qualifier column AFTER the amount did not:
       *
       *   | Policy Limit of Liability | $28,995,637 | being 100% | per occurrence ... |
       *   | Exposed Limits | Building | $26,582,592 | per Occurrence |
       *
       * which is every row of a property schedule of limits. So a subscription
       * property policy stating a $28,995,637 policy limit, a $26,582,592
       * building limit and $1,102,500 of personal property reported no limits
       * at all, and the report said the declarations state no field for them.
       *
       * Collapse inside each cell, then join the cells. */
      return l.replace(/^\s*\|/, '').replace(/\|\s*$/, '')
              .split('|')
              .map(c => c.replace(/[ \t]+/g, ' ').trim())
              .join('   ')
              .trim();
    }).join('\n');
  }

  // -----------------------------------------------------------------------
  // 1. LABEL — what carriers call the prior-acts anchor.
  //
  // `canonical` is true only for wording that can mean nothing else. The rest
  // are read as the anchor but always PRINTED under their own label, because
  // a handful of forms use "Continuity Date" for the prior-and-pending
  // exclusion instead. Printing the carrier's word makes that visible rather
  // than silently wrong.
  // -----------------------------------------------------------------------
  const RETRO_LABELS = [
    { re: /\bretroactive\s+(?:coverage\s+)?date\b/i,        label: 'Retroactive Date',        canonical: true  },
    { re: /\bretro(?:active)?\.?\s*date\b/i,                label: 'Retro Date',              canonical: true  },
    { re: /\bcontinuity\s+date\b/i,                         label: 'Continuity Date',         canonical: false },
    { re: /\bprior\s+acts?\s+(?:coverage\s+)?date\b/i,      label: 'Prior Acts Date',         canonical: true  },
    { re: /\bcontinuous\s+coverage\s+date\b/i,              label: 'Continuous Coverage Date',canonical: false },
    { re: /\b(?:first|original)\s+inception\s+date\b/i,     label: 'First Inception Date',    canonical: false },
    { re: /\bloss\s+inclusion\s+date\b/i,                   label: 'Loss Inclusion Date',     canonical: false },
    { re: /\bcoverage\s+commencement\s+date\b/i,            label: 'Coverage Commencement Date', canonical: false }
  ];

  // -----------------------------------------------------------------------
  // 3. CONCEPT — dates that sit next to the anchor and are NOT the anchor.
  //
  // Tested against the matched label itself and the ~60 characters in front
  // of it, because these appear as qualifiers ("Pending or Prior Proceeding
  // Date") rather than as separate items.
  // -----------------------------------------------------------------------
  const NOT_RETRO = [
    /\b(?:prior|pending)\s+(?:and|or|\/)\s*(?:prior|pending)\b/i,   // Prior and Pending Litigation Date
    /\bpending\s+(?:litigation|proceeding|claim)/i,
    /\bknowledge\s+date\b/i,
    /\bwarrant(?:y|ed)\s+date\b/i,
    /\bpolicy\s+(?:effective|expiration|expiry|renewal)\s+date\b/i,
    /\bdate\s+(?:issued|of\s+issue|prepared|created|printed)\b/i,
    /\bbirth\s*date\b|\bdate\s+of\s+birth\b/i,
    /\bhire\s+date\b/i
  ];

  // -----------------------------------------------------------------------
  // 2. VALUE — the date grammars that actually appear on declarations pages,
  // plus the non-date answers, which are answers and must not be treated as
  // silence.
  // -----------------------------------------------------------------------
  const MONTHS = { jan:'01', feb:'02', mar:'03', apr:'04', may:'05', jun:'06',
                   jul:'07', aug:'08', sep:'09', oct:'10', nov:'11', dec:'12' };
  const MON = '(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*';

  const DATE_FORMS = [
    // 07/01/2012, 8-24-25, 07.01.2012
    { re: new RegExp('\\b(0?[1-9]|1[0-2])[\\/\\-.](0?[1-9]|[12]\\d|3[01])[\\/\\-.]((?:19|20)?\\d{2})\\b'),
      take: m => [m[1], m[2], m[3]] },
    // 01-Jul-2020, 1 July 2020, 01 Jul 20  (London / Bermuda / Beazley)
    { re: new RegExp('\\b(0?[1-9]|[12]\\d|3[01])[\\s\\-\\/.]*(' + MON + ')[\\s\\-\\/.,]*((?:19|20)?\\d{2})\\b', 'i'),
      take: m => [MONTHS[m[2].slice(0,3).toLowerCase()], m[1], m[3]] },
    // July 1, 2020 / Jul-01-2020
    { re: new RegExp('\\b(' + MON + ')[\\s\\-\\/.,]*(0?[1-9]|[12]\\d|3[01])(?:st|nd|rd|th)?[\\s\\-\\/.,]+((?:19|20)\\d{2})\\b', 'i'),
      take: m => [MONTHS[m[1].slice(0,3).toLowerCase()], m[2], m[3]] },
    // 2020-07-01 (ISO)
    { re: new RegExp('\\b((?:19|20)\\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01])\\b'),
      take: m => [m[2], m[3], m[1]] }
  ];

  // A stated answer that is not a date. "Full prior acts" is the BEST possible
  // answer and was previously indistinguishable from finding nothing.
  const VALUE_WORDS = [
    { re: /\bfull\s+prior\s+acts?\b|\bunlimited\s+(?:prior|retro)\w*\b|\bno\s+retroactive\s+date\b|\bprior\s+acts?\s+(?:are\s+)?covered\b/i,
      value: 'Full Prior Acts', kind: 'full-prior-acts' },
    { re: /\bnot\s+applicable\b|\bn\s*\/\s*a\b|\bnone\b|\bnil\b/i,
      value: 'Not applicable (stated)', kind: 'not-applicable' },
    // Deliberately narrow. An earlier draft accepted a bare "inception"
    // anywhere in the window and lit up on the claims-made explanatory notice
    // every carrier bolts onto a package policy, inventing a retro date for
    // coverages that have none. It must read as an ANSWER in the value
    // column, not as a word that happens to be nearby.
    { re: /^[^A-Za-z0-9]{0,12}(?:same\s+as\s+)?(?:the\s+)?(?:policy\s+)?inception(?:\s+date)?\b|^[^A-Za-z0-9]{0,12}no\s+prior\s+acts?\b/i,
      value: 'Policy inception (no prior acts)', kind: 'inception' }
  ];

  /** Normalise any recognised date to mm/dd/yyyy so 01-Jul-2020, July 1 2020
   *  and 07/01/20 compare equal. Returns '' when nothing parses. */
  function normalizeDate(s) {
    const str = String(s || '').trim();
    if (!str) return '';
    for (const f of DATE_FORMS) {
      const m = f.re.exec(str);
      if (!m) continue;
      const parts = f.take(m);
      if (!parts || !parts[0]) continue;
      let [mm, dd, yy] = parts;
      mm = String(mm).padStart(2, '0');
      dd = String(dd).padStart(2, '0');
      yy = String(yy);
      if (yy.length === 2) yy = (parseInt(yy, 10) < 80 ? '20' : '19') + yy;
      return mm + '/' + dd + '/' + yy;
    }
    return '';
  }

  /** The first recognised date anywhere in a span, with its original spelling
   *  kept so the report can quote the policy rather than paraphrase it. */
  function findDate(span) {
    const s = String(span || '');
    let best = null;
    for (const f of DATE_FORMS) {
      const m = f.re.exec(s);
      if (!m) continue;
      if (best === null || m.index < best.index) best = { index: m.index, raw: m[0] };
    }
    if (!best) return null;
    const norm = normalizeDate(best.raw);
    return norm ? { raw: best.raw.trim(), normalized: norm, index: best.index } : null;
  }

  /** True when this label occurrence is a look-alike rather than the anchor.
   *
   *  The decoy wording is a QUALIFIER on the label — "Prior and Pending
   *  Litigation Date", "Knowledge Date" — so it sits immediately in front of
   *  it, in the same declarations item. A flat 60-character lookbehind reached
   *  back past the item boundary instead, and a decoy on the line above
   *  suppressed the genuine anchor on the line below:
   *
   *      Prior and Pending Litigation Date: 01-Jul-2019
   *      Continuity Date:                   01-Jul-2020   <- vetoed, wrongly
   *
   *  So the probe stops at the end of the previous item, which is whichever
   *  comes last: a line break, or a date (a date means the item before this
   *  one already got its answer). */
  function isDecoy(matchedLabel, lead) {
    let seg = String(lead || '');
    const nl = seg.lastIndexOf('\n');
    if (nl >= 0) seg = seg.slice(nl + 1);
    const d = findDate(seg);
    if (d) seg = seg.slice(d.index + d.raw.length);
    const probe = seg.slice(-60) + ' ' + String(matchedLabel || '');
    return NOT_RETRO.some(re => re.test(probe));
  }

  /**
   * Read every prior-acts anchor stated in a block of text.
   *
   * Returns [{ label, value, normalized, kind, canonical, index }] where
   * `label` is the carrier's own wording and `kind` is one of
   * 'date' | 'full-prior-acts' | 'not-applicable' | 'inception'.
   *
   * `window` is how far past the label to look for the value. Declarations
   * pages come out of PDF.js with the label and its value 50-300 characters
   * apart when the page is two-column, so it is deliberately generous; the
   * decoy check is what keeps that from dragging in a neighbour's date.
   */
  function readRetroAnchors(text, opts) {
    const src = spTableLines(text);
    if (!src) return [];
    const win = (opts && opts.window) || 220;
    const out = [];
    const seen = new Set();
    // One anchor per position. RETRO_LABELS is ordered most-specific first, so
    // "Retroactive Date" claims the offset before the shorter "Retro Date"
    // pattern reaches it and reports the same value twice.
    const claimed = new Set();

    RETRO_LABELS.forEach(def => {
      const re = new RegExp(def.re.source, 'gi');
      let m;
      while ((m = re.exec(src)) !== null) {
        if (claimed.has(m.index)) continue;
        const lead = src.slice(Math.max(0, m.index - 80), m.index);
        if (isDecoy(m[0], lead)) continue;
        const after = src.slice(m.index + m[0].length, m.index + m[0].length + win);
        // A stated word answer beats a date: a form that says "Full Prior
        // Acts" often prints the policy inception date two lines below, and
        // taking the date there would understate the cover the insured has.
        let hit = null;
        for (const v of VALUE_WORDS) {
          const vm = v.re.exec(after);
          if (!vm) continue;
          const d = findDate(after);
          if (d && d.index < vm.index) break;   // the date came first; it wins
          hit = { value: v.value, normalized: v.kind === 'full-prior-acts' ? 'FULL_PRIOR_ACTS' : v.kind.toUpperCase().replace(/-/g, '_'), kind: v.kind };
          break;
        }
        if (!hit) {
          const d = findDate(after);
          if (d) hit = { value: d.raw, normalized: d.normalized, kind: 'date' };
        }
        if (!hit) continue;
        // Canonical wordings are one concept, so they share a dedupe key; a
        // non-canonical label keeps its own, because the whole point of
        // carrying "Continuity Date" separately is that a reader can see it.
        //
        // When attributing, the coverage joins the key. Aspen View prints
        // 07/01/2012 three times, once each under Educators Legal Liability,
        // D&O and EPLI. Those are three findings about three coverages, not
        // one finding repeated, and collapsing them loses two real retro
        // dates from the report.
        const cov = opts && opts.attribute ? coverageAt(src, m.index, opts) : null;
        const key = (def.canonical ? 'canonical' : def.label) + '|' + hit.normalized + '|' + (cov || '');
        if (seen.has(key)) continue;
        seen.add(key);
        claimed.add(m.index);
        out.push({ label: def.label, canonical: def.canonical, index: m.index,
                   coverage: cov,
                   value: hit.value, normalized: hit.normalized, kind: hit.kind });
      }
    });

    out.sort((a, b) => a.index - b.index);

    // A canonical label present in the same text DEMOTES the synonyms.
    //
    // Reading "Continuity Date" as the prior-acts anchor is right on a Beazley
    // Breach Response declarations, which states no Retroactive Date at all.
    // It is wrong on a form that states both:
    //
    //     Item 9.  Retroactive Date   Full Prior Acts Coverage
    //     Item 10. Continuity Date    August 07, 2025
    //
    // There the Continuity Date is the prior-and-pending / knowledge anchor,
    // a different term, and promoting it would replace "Full Prior Acts" —
    // the best answer a claims-made policy can give — with a 2025 date that
    // reads as almost no prior acts cover at all. So a synonym is only the
    // anchor when the document offers nothing better.
    if (out.some(a => a.canonical)) {
      const only = out.filter(a => a.canonical);
      if (only.length) return only;
    }
    return out;
  }

  // -----------------------------------------------------------------------
  // The extended reporting period, by the same three axes.
  //
  // The exclusion at the bottom matters as much as the list above it. A
  // Commercial Crime DISCOVERY form carries an "Extended Period To Discover
  // Loss" — 60 days after cancellation, built in, not purchasable. It is not
  // an ERP, and a policy that has one has not answered the tail question.
  // Matching it would have put a ✓ on Aspen View's crime row, which is
  // exactly the "we found nothing, so say nothing is there" inversion that
  // keeps coming back (issue #15).
  // -----------------------------------------------------------------------
  const ERP_LABELS = [
    { re: /\bextended\s+reporting\s+period\b/i,                 label: 'Extended Reporting Period' },
    { re: /\b(?:supplemental|automatic|basic|optional)\s+extended\s+reporting\s+period\b/i, label: 'Extended Reporting Period' },
    { re: /\bextended\s+(?:claims?\s+)?reporting\s+(?:period|provision|endorsement)\b/i, label: 'Extended Reporting Period' },
    { re: /\boptional\s+extension\s+period\b/i,                 label: 'Optional Extension Period' },
    { re: /\bdiscovery\s+period\b/i,                            label: 'Discovery Period' },
    { re: /\bextended\s+discovery\s+(?:period|clause)\b/i,      label: 'Discovery Period' },
    { re: /\brun[\s\-]?off\s+(?:period|coverage|endorsement)\b/i, label: 'Run-Off Coverage' },
    { re: /\btail\s+(?:coverage|period|endorsement)\b/i,        label: 'Tail Coverage' },
    { re: /\bS\.?E\.?R\.?P\.?\b/,                               label: 'Extended Reporting Period' },
    { re: /\bE\.?R\.?P\.?\b/,                                   label: 'Extended Reporting Period' }
  ];

  const NOT_ERP = [
    /\bextended\s+period\s+to\s+discover\s+loss\b/i,   // crime discovery form
    /\bdiscover\w*\s+(?:of\s+)?loss\b/i
  ];

  // Nearly every package and umbrella policy carries a consumer notice that
  // EXPLAINS claims-made cover and extended reporting periods in the abstract.
  // Aspen View's umbrella is occurrence follow-form with no ERP of its own,
  // and the scan reported one anyway, off "extended reporting period coverage
  // must be offered to you by law for a duration of at least..." in that
  // notice. Boilerplate about what a policy of some other kind would provide
  // is not a term of this policy. These markers are matched against a wide
  // span around the hit, because the notice is prose and the giveaway
  // sentence can be a paragraph away from the words "extended reporting
  // period".
  const GENERIC_NOTICE = [
    /\bmust\s+be\s+offered\s+to\s+you\s+by\s+law\b/i,
    /\bif\s+you\s+switch\s+from\s+an\s+occurrence\s+policy\b/i,
    /\bcarefully\s+review\s+your\s+policy\b/i,
    /\bthis\s+notice\s+(?:is|does)\s+not\b/i,
    /\bpolicyholder\s+notice\b/i,
    /\bimportant\s+notice\s+to\s+all\b/i,
    /\bconsult\s+your\s+(?:agent|broker|insurance\s+advisor)\b/i
  ];

  function inGenericNotice(src, index) {
    const span = src.slice(Math.max(0, index - 1400), index + 900);
    return GENERIC_NOTICE.some(re => re.test(span));
  }

  // A number beside an ERP label is usually NOT its length.
  //
  // Aspen View's package policy says "Supplemental Extended Reporting Period,
  // you must request it in writing and pay the additional premium due, within
  // 60 days after the effective date" — that 60 days is the ELECTION
  // DEADLINE. The umbrella's nearest number sits in "...applies separately to
  // each consecutive annual period" twelve months later, about the aggregate
  // limit. Taking either as the tail length reports a 60-day tail on a policy
  // that has a one-year one.
  //
  // So a duration counts only when the wording binds it to the length of the
  // period: a declarations schedule pairing it with a premium percentage
  // ("12 Months for 100% of the Annual Policy Premium"), or a length
  // preposition in front of it. A deadline preposition vetoes it outright.
  // When nothing qualifies we return the ERP with a null duration, which the
  // report prints as "term not stated on the pages read" — present, length
  // unknown. That is a different claim from absent, and it is the true one.
  const DEADLINE_LEAD = /\b(?:within|no\s+later\s+than|not\s+later\s+than|prior\s+to|before|after|up\s+to)\s*$/i;
  const LENGTH_LEAD   = /\b(?:of|for\s+a\s+period\s+of|for\s+an\s+additional|period\s+of|shall\s+be|will\s+be|is|:|–|—|-)\s*$/i;

  function erpDuration(after, pct) {
    const re = /(\d{1,3})\s*(?:-|\s)?\s*(month|year|day)s?\b/gi;
    let m;
    while ((m = re.exec(after)) !== null) {
      const lead = after.slice(Math.max(0, m.index - 40), m.index).replace(/\s+$/, ' ');
      if (DEADLINE_LEAD.test(lead)) continue;
      // Paired with a premium percentage in the same clause: the schedule form.
      const pairedWithPct = pct && Math.abs(pct.index - m.index) < 60;
      if (!pairedWithPct && !LENGTH_LEAD.test(lead)) continue;
      const n = Number(m[1]);
      return {
        text: m[0].replace(/\s+/g, ' ').trim(),
        months: /year/i.test(m[2]) ? n * 12 : /day/i.test(m[2]) ? Math.round(n / 30) : n
      };
    }
    return null;
  }

  /** The ERP a policy actually offers, with the carrier's own wording, its
   *  duration and its cost where those are stated adjacently. */
  function readErp(text, opts) {
    const src = spTableLines(text);
    if (!src) return null;
    const win = (opts && opts.window) || 260;
    // Every mention, then the best one — NOT the first label in the list that
    // hits anywhere in the file. Reading the Beazley cyber policy by list
    // order returned a bare "Extended Reporting Period" lifted from the
    // Colorado policyholder notice near the back, when the declarations on
    // page 2 state "Optional Extension Period — 12 Months for 100% of the
    // Annual Policy Premium". Same concept, but the answer with the term and
    // the price is the one a broker needs, and it is the one the policy leads
    // with. Score on how much of the answer came with it, tie-break earliest.
    const hits = [];
    for (const def of ERP_LABELS) {
      const re = new RegExp(def.re.source, def.re.flags.includes('i') ? 'gi' : 'g');
      let m;
      while ((m = re.exec(src)) !== null) {
        const lead = src.slice(Math.max(0, m.index - 70), m.index);
        if (NOT_ERP.some(x => x.test(lead + ' ' + m[0]))) continue;
        if (inGenericNotice(src, m.index)) continue;
        const after = src.slice(m.index + m[0].length, m.index + m[0].length + win);
        const pct = /(\d{1,3}(?:\.\d+)?)\s*%/.exec(after);
        const dur = erpDuration(after, pct);
        hits.push({
          label: def.label,
          stated: true,
          months: dur ? dur.months : null,
          duration: dur ? dur.text : null,
          premiumPct: pct ? Number(pct[1]) : null,
          index: m.index,
          form: formAt(src, m.index),
          score: (dur ? 2 : 0) + (pct ? 1 : 0),
          quote: (m[0] + after.slice(0, 120)).replace(/\s+/g, ' ').trim()
        });
      }
    }
    if (!hits.length) return null;
    hits.sort((a, b) => (b.score - a.score) || (a.index - b.index));
    return hits[0];
  }

  // -----------------------------------------------------------------------
  // ATTRIBUTION — which coverage does this anchor belong to?
  //
  // This is the failure that put "General Liability — Claims-Made — retro
  // 07/01/2012" in Aspen View's Section 5C. The GL is CG 00 01, the ISO
  // OCCURRENCE form, and has no retro date. 07/01/2012 is real, but it
  // belongs to three claims-made coverages printed further down the same
  // Commercial General Liability Coverage Part declarations page:
  //
  //     Educators Management Liability Coverage
  //         Coverage EL - Educators Legal Liability
  //               Retroactive Date            07/01/2012
  //         Coverage DO - Directors And Officers Liability
  //               Retroactive Date            07/01/2012
  //         Coverage EP - Employment Practices Liability
  //               Retroactive Date            07/01/2012
  //     Employee Benefits Liability Coverage
  //               Retroactive Date            07/01/2014
  //
  // Binding to the COVERAGE PART heading gives every one of those to General
  // Liability. Binding to the nearest preceding COVERAGE BLOCK gives each to
  // its owner, and leaves GL with nothing, which is the correct answer. The
  // same rule is what scoped the umbrella limits away from the schedule of
  // underlying insurance: the enclosing heading is not the nearest heading.
  // -----------------------------------------------------------------------
  const COVERAGE_BLOCK = [
    // "Coverage EL - Educators Legal Liability", "Coverage DO: Directors And Officers"
    /^[\s|]*coverage\s+[A-Z]{1,3}\s*[-–—:]\s*([A-Za-z][A-Za-z&'\/ ]{3,60})/i,
    // "Employee Benefits Liability Coverage", "Cyber Liability Coverage Part"
    /^[\s|]*([A-Z][A-Za-z&'\/ ]{3,60}?\s+(?:Liability|Crime|Fidelity|Pollution|Professional))\s+Coverage(?:\s+(?:Part|Form|Section))?\b/,
    // A bare declarations sub-heading in caps
    /^[\s|]*([A-Z][A-Z&'\/ ]{6,60}?)\s+(?:COVERAGE|DECLARATIONS)\b/,
    /* A SHORT NAME FOLLOWED BY THE WORDS "COVERAGE PART".
     *
     * "CRIME COVERAGE PART" matched nothing above: the pattern before it
     * wants a qualifying word ahead of Crime, and the caps rule wants a name
     * of seven characters. So a crime part's own insuring clause could be
     * read and then attributed to no coverage at all, which left it unusable.
     * Requiring the literal words "Coverage Part" or "Coverage Form" keeps
     * this from matching ordinary prose. */
    /^[\s|]*([A-Z][A-Za-z&'\/ ]{2,60}?)\s+Coverage\s+(?:Part|Form)\b/i,
    /* THE TRIGGER PRINTED INSIDE THE HEADING.
     *
     * Hanover's Eldercare Advantage declarations name each part with its
     * trigger between the name and "Coverage Part":
     *
     *     Commercial General Liability Claims-Made  Coverage Part
     *     ...
     *     Retroactive Date 07/20/2021
     *     Eldercare  Professional Liability  Claims-Made  Coverage Part
     *     ...
     *     Retroactive Date 07/20/2021
     *
     * The hyphen in "Claims-Made" is outside every name class above, so
     * neither heading matched, the GL anchor was attributed to no coverage,
     * and Ohio Eastern Star's 5C printed "No prior-acts anchor stated" for a
     * General Liability part whose retro date sat one line beneath it. The
     * trigger word is not part of the coverage's name, so it is consumed here
     * and left out of the capture. */
    /^[\s|]*([A-Z][A-Za-z&'\/ ]{2,60}?)\s*[-–—]?\s*(?:claims[\s\-]*made(?:\s+and\s+reported)?|occurrence)\s+Coverage\s+(?:Part|Form|Endorsement)\b/i,
    // An ENDORSEMENT that names the coverage it provides:
    // "EMPLOYEE BENEFITS LIABILITY ENDORSEMENT". Only accepted when the name
    // resolves to a coverage this engine knows, because "Administrative
    // Change Endorsement" names no coverage and must not claim the block.
    [/^[\s|]*([A-Za-z][A-Za-z&'\/ ]{4,60}?)\s+(?:COVERAGE\s+)?ENDORSEMENT\s*$/i, 'mustResolve']
  ];

  /** The coverage block a character offset sits inside: the nearest heading
   *  ABOVE it, searched line by line, capped so an anchor with no heading
   *  above it reports null rather than reaching across the document. */
  // An endorsement's APPLICABILITY line is not its owner.
  //
  //     # **EMPLOYEE BENEFITS LIABILITY ENDORSEMENT**      <- what it IS
  //     THIS ENDORSEMENT PROVIDES CLAIMS-MADE COVERAGE.
  //     This endorsement modifies insurance provided under the following:
  //        GENERAL LIABILITY COVERAGE PART                  <- what it CHANGES
  //     | Employee Benefits Programs | $1,000,000 | ... |
  //     | Retroactive Date:          | 11/09/2025 | ... |   <- the anchor
  //
  // Walking back from the anchor hits the applicability line first, so this
  // policy's Employee Benefits retro date was attributed to GENERAL
  // LIABILITY. That is not a cosmetic mislabel: the rule that a document
  // naming coverages for its anchors has answered for every coverage in it
  // then denies the date to Employee Benefits, Public Officials, EPLI and D&O
  // alike - four claims-made coverages reporting no retro date while the date
  // sat two lines from each of them.
  //
  // Same distinction Section 7 already draws. A line sitting under "modifies
  // insurance provided under the following" is skipped, and the walk
  // continues to the heading that names the endorsement itself.
  const _APPLIES_TO = /\bthis\s+endorsement\s+(?:modifies|changes)[^\n]{0,80}(?:provided\s+under\s+the\s+following|the\s+following)\s*:?/i;

  function coverageAt(text, index, opts) {
    const back = (opts && opts.lookback) || 2500;
    const head = String(text || '').slice(Math.max(0, index - back), index);
    const lines = head.split('\n');
    // Which lines sit inside an applicability list: everything from the
    // preamble until the first line that is not a coverage-part entry.
    const inApplicability = new Array(lines.length).fill(false);
    for (let i = 0; i < lines.length; i++) {
      if (!_APPLIES_TO.test(lines[i])) continue;
      for (let j = i + 1; j < lines.length && j <= i + 20; j++) {
        const l = lines[j].trim();
        if (!l) continue;
        if (!/coverage\s+(?:part|form)\s*$|\bpolicy\s*$/i.test(l)) break;
        inApplicability[j] = true;
      }
    }
    for (let i = lines.length - 1; i >= 0; i--) {
      if (inApplicability[i]) continue;
      for (const entry of COVERAGE_BLOCK) {
        const re = Array.isArray(entry) ? entry[0] : entry;
        const mustResolve = Array.isArray(entry) && entry[1] === 'mustResolve';
        const m = re.exec(lines[i]);
        if (!m || !m[1]) continue;
        const name = m[1].replace(/\s+/g, ' ').trim();
        if (name.length < 4) continue;
        /* A sentence, not a heading. Cincinnati's coverage forms open "THIS
         * COVERAGE PART PROVIDES CLAIMS-MADE COVERAGE…", and "THIS" became the
         * coverage that owned the D&O declarations' Retroactive Date, so
         * neither D&O nor EPL could find their own anchor. */
        if (/^(?:this|the|that|each|any|all|your|our|such|which|other|such)$/i.test(name)) continue;
        if (mustResolve) {
          // `root`, not `window`: coverageTerms registers on globalThis when
          // there is no window, so checking `window` made this branch dead
          // outside a browser — and silently, by falling through to null.
          const CT = root.spCoverageTerms || null;
          if (!CT || !CT.canonical(name)) continue;
        }
        return name;
      }
    }
    return null;
  }

  /** readRetroAnchors + the coverage each one belongs to. */
  function readAttributedAnchors(text, opts) {
    return readRetroAnchors(text, Object.assign({}, opts, { attribute: true }));
  }

  // -----------------------------------------------------------------------
  // THE FORMS SCHEDULE IS THE AUTHORITY ON THE TRIGGER.
  //
  // Weighing the words "occurrence" and "claims-made" near a coverage-part
  // heading is guessing, and on Aspen View it guessed wrong in the most
  // damaging direction: General Liability came back Claims-Made. It is not.
  // The policy says so itself, on the forms schedule, in one line:
  //
  //     CG 00 01          04 13        Commercial General Liability Coverage Form
  //
  // CG 00 01 is the ISO OCCURRENCE form. CG 00 02 is the claims-made one, and
  // it appears nowhere in this policy. A GL reported as claims-made then drags
  // a retro date in behind it, because retro dates are only looked up for
  // claims-made parts — so one wrong trigger manufactures a second wrong fact
  // and prints both with a citation.
  //
  // Better still, the schedule usually states the trigger in the TITLE, which
  // needs no lookup table and works for carrier-proprietary forms:
  //
  //     GCG 73 11   07 14   Employee Benefits Liability Coverage (Claims-Made)
  //     GCG 74 10   07 14   Sexual Misconduct Liability Coverage (Occurrence)
  //     CR 00 20    08 13   Commercial Crime Coverage Form (Discovery Form)
  //
  // That last one is why the trigger is not a boolean. A crime discovery form
  // is neither occurrence nor claims-made: it responds to when you DISCOVER
  // the loss. Forcing it into the binary is what put "Crime / Fidelity —
  // Claims-Made" in the report, and with it a retro-date gap for a form that
  // cannot have a retro date.
  // -----------------------------------------------------------------------
  const ISO_FORM_TRIGGER = {
    'CG0001': 'Occurrence',      // Commercial General Liability
    'CG0002': 'Claims-Made',     // Commercial General Liability (claims-made)
    'CA0001': 'Occurrence',      // Business Auto
    'CU0001': 'Occurrence',      // Commercial Liability Umbrella
    'CR0020': 'Discovery',       // Commercial Crime (discovery form)
    'CR0021': 'Loss Sustained'   // Commercial Crime (loss sustained form)
  };

  const TITLE_TRIGGER = /\((claims[\s\-]?made(?:\s+and\s+reported)?|occurrence|discovery(?:\s+form)?|loss\s+sustained(?:\s+form)?)\)/i;
  const FORM_LINE = /^[\s|]*([A-Z]{1,4}[\s\-]?\d{2}[\s\-]?\d{2}(?:[\s\-]?\d{2})?)\s+(?:\d{2}[\s\-]?\d{2}\s+)?([A-Z][^|]{6,90})$/;

  function normTrigger(word) {
    const w = String(word || '').toLowerCase();
    if (/claims/.test(w)) return 'Claims-Made';
    if (/occurrence/.test(w)) return 'Occurrence';
    if (/discovery/.test(w)) return 'Discovery';
    if (/loss\s*sustained/.test(w)) return 'Loss Sustained';
    return null;
  }

  /** Every line of the forms schedule: form number, title, and the coverage
   *  the title names. Used to bind a provision found in a form body back to
   *  the coverage that form provides. */
  function readFormsSchedule(text) {
    const out = [];
    const seen = new Set();
    spTableLines(text).split('\n').forEach(line => {
      const m = FORM_LINE.exec(line.replace(/\s+$/, ''));
      if (!m) return;
      const form = m[1].replace(/[\s\-]/g, '').toUpperCase();
      if (seen.has(form)) return;
      const title = m[2].replace(/\s+/g, ' ').trim();
      if (!/\b(?:coverage|liability|crime|endorsement|form|part)\b/i.test(title)) return;
      seen.add(form);
      out.push({ form, title, coverage: title
        .replace(TITLE_TRIGGER, '')
        .replace(/\b(?:coverage|insurance)\s+(?:form|part|section|endorsement)\b/ig, '')
        .replace(/\b(?:coverage|form|part|endorsement)\b\s*$/i, '')
        .replace(/\s+/g, ' ').trim() });
    });
    return out;
  }

  /** The form number a character offset sits inside. Policy forms print their
   *  number in the footer of every page, so the nearest one above a provision
   *  identifies the form that provides it. */
  function formAt(text, index, opts) {
    const back = (opts && opts.lookback) || 3000;
    const span = String(text || '').slice(Math.max(0, index - back), index + 400);
    const all = [...span.matchAll(/\b([A-Z]{2,4}\s?\d{2}\s?\d{2})\b/g)];
    if (!all.length) return null;
    return all[all.length - 1][1].replace(/\s/g, '').toUpperCase();
  }

  /** Every coverage form the policy schedules, with the trigger the schedule
   *  states or the form number implies.
   *  Returns [{ form, title, coverage, trigger, source: 'title' | 'form-number' }] */
  function readFormTriggers(text) {
    const out = [];
    const seen = new Set();
    spTableLines(text).split('\n').forEach(line => {
      const m = FORM_LINE.exec(line.replace(/\s+$/, ''));
      if (!m) return;
      const form = m[1].replace(/[\s\-]/g, '').toUpperCase();
      const title = m[2].replace(/\s+/g, ' ').trim();
      if (!/\b(?:coverage|liability|crime|form|part)\b/i.test(title)) return;
      const tm = TITLE_TRIGGER.exec(title);
      const trigger = tm ? normTrigger(tm[1]) : (ISO_FORM_TRIGGER[form] || null);
      if (!trigger) return;
      // The coverage this form provides, with the form furniture stripped.
      const coverage = title
        .replace(TITLE_TRIGGER, '')
        .replace(/\b(?:coverage|insurance)\s+(?:form|part|section)\b/ig, '')
        .replace(/\b(?:coverage|form|part)\b\s*$/i, '')
        .replace(/\s+/g, ' ').trim();
      const key = form + '|' + trigger;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ form, title, coverage, trigger, source: tm ? 'title' : 'form-number' });
    });
    return out;
  }

  // -----------------------------------------------------------------------
  // PAIRED TABLE VALUES — read a table as a table.
  //
  // Surplus-lines and London-market declarations state limits in a grid whose
  // labels and amounts live in DIFFERENT CELLS. On Tulsa Honors the gap
  // between the word "Building" and its own limit of $26,582,592 measured 610
  // characters; every property pattern in this engine allows sixty. The real
  // figures were not merely missed, they were never candidates, and the
  // scan settled on a supplemental sublimit that happened to sit near a
  // matching word. A row reading "Building $100,000" on a school with $26.5M
  // of buildings is worse than a blank.
  //
  // Proximity cannot fix that, because once a table puts a label and its value
  // in different cells there is no proximity to measure. The structure is the
  // meaning: within one table row, the Nth item of a column corresponds to the
  // Nth item of the next.
  //
  //   | Policy Limit of Liability | $28,995,637       | being 100%   |
  //   | Exposed Limits            | Building          | $26,582,592  |
  //   |                           | Personal Property | $1,102,500   |
  //   |                           | Time Element      | $1,310,545   |
  //
  // LlamaParse packs each column into one cell with <br/> between the items,
  // and drops some of those breaks, concatenating neighbours:
  // "Personal PropertyTime Element", "$1,102,500$1,310,545". Those are split
  // back apart on the two boundaries that cannot occur inside one value — a
  // dollar amount immediately followed by another, and a lowercase letter
  // immediately followed by an uppercase one.
  // -----------------------------------------------------------------------
  const _MONEY_ONLY = /^\$?\s*[\d][\d,]*(?:\.\d{2})?$/;

  function splitRunTogether(item) {
    return String(item)
      .replace(/(\$[\d][\d,]*(?:\.\d{2})?)(?=\$)/g, '$1\u0000')
      .replace(/([a-z])(?=[A-Z])/g, '$1\u0000')
      .split('\u0000')
      .map(x => x.trim())
      .filter(Boolean);
  }

  function cellItems(cell) {
    return String(cell)
      .split(/<br\s*\/?>/i)
      .flatMap(splitRunTogether)
      .map(x => x.replace(/\*\*/g, '').trim())
      .filter(Boolean);
  }

  /** Label/value pairs read from the grid, for tables whose labels and
   *  amounts sit in separate columns. */
  function readPairedTableValues(text) {
    const out = [];
    String(text || '').split('\n').forEach(line => {
      if (line.indexOf('|') < 0 || !/<br\s*\/?>/i.test(line)) return;
      if (/^[\s|:\-]+$/.test(line)) return;
      const cols = line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(cellItems);
      if (cols.length < 2) return;
      for (let c = 0; c < cols.length - 1; c++) {
        const labels = cols[c], values = cols[c + 1];
        const n = Math.min(labels.length, values.length);
        for (let k = 0; k < n; k++) {
          const label = labels[k], value = values[k];
          if (!label || !value) continue;
          if (_MONEY_ONLY.test(label)) continue;          // a label is not a figure
          if (!_MONEY_ONLY.test(value)) continue;         // and a value is
          if (label.length < 3 || label.length > 60) continue;
          out.push({ label: label, value: value.replace(/^\s*\$?\s*/, '$') });
        }
      }
    });
    return out;
  }

  /* A FORM THAT ANCHORS PRIOR ACTS A DIFFERENT WAY HAS NOT OMITTED ANYTHING.
   *
   * Matrix Human Services' Chubb ForeFront Portfolio carries no Retroactive
   * Date anywhere — the word does not appear in the document. Each liability
   * coverage part instead declares:
   *
   *     (E) Pending or Prior Proceedings Date: May 1, 2005
   *
   * NOT_RETRO already refuses to read that as a retro date, and it is right to:
   * they are different things. A retroactive date is the earliest wrongful act
   * that can be covered; a pending-or-prior date is the cut-off for litigation
   * already on foot. But the report then said "Retro Date Not Found", which
   * sends a broker chasing a missing item on a policy that has FULL PRIOR ACTS
   * — a better position than a retro date, reported as a gap.
   *
   * Reading the decoy as the answer is a bug. Knowing the decoy is there, and
   * saying what it means, is the finding.
   */
  const PRIOR_PROCEEDINGS = [
    /\bpending\s+(?:or|and|\/)\s*prior\s+(?:proceedings?|litigation|claims?)/i,
    /\bprior\s+(?:or|and|\/)\s*pending\s+(?:proceedings?|litigation|claims?)/i,
    // Cincinnati's management liability declarations: "Prior or Pending
    // Date: 11-16-2005", with no proceedings/litigation word.
    /\bprior\s+(?:or|and|\/)\s*pending\s+date\b/i,
    /\bpending\s+(?:or|and|\/)\s*prior\s+date\b/i
  ];

  /** Every "Pending or Prior Proceedings Date" stated in the text.
   *
   *  Returned separately from the retro anchors and never merged with them:
   *  the caller reports it as what it is, not as a retroactive date. */
  /* AN ANCHOR THAT IS A CONDITION RATHER THAN A DATE.
   *
   * Corvus Smart Cyber states no retroactive date — its declarations have
   * nine items and none of them is one. Prior acts are limited instead by
   * what was known at inception:
   *
   *   Prior Knowledge or Notification — Any act, fact, error, omission ...
   *   that could reasonably be expected to give rise to a Claim or Loss when
   *   such ... was known prior to the Inception Date of this Policy by any
   *   member of the Control Group
   *
   * So the policy DOES limit how far back cover reaches; it just does not do
   * it with a date field. Reporting "Retro Date Not Found" against that hides
   * the limitation a broker has to check, in the same way "Retro Date Not
   * Found" hid Chubb's Pending or Prior Proceedings Date.
   *
   * The value is the inception date where the declarations state one, because
   * that is the point the condition turns on.
   */
  const _PRIOR_KNOWLEDGE = /prior\s+knowledge(?:\s+or\s+notification)?\b/i;
  const _KNOWN_BEFORE = /\bknown\s+prior\s+to\s+the\s+(inception|effective)\s+date\b/i;

  function readPriorKnowledge(text) {
    const src = spTableLines(text);
    if (!src) return null;
    const flat = src.replace(/-\s*\n\s*/g, '').replace(/\s+/g, ' ');
    const m = _KNOWN_BEFORE.exec(flat);
    if (!m) return null;
    // Require the provision to be titled as such, so ordinary prose that
    // happens to mention knowledge before inception does not qualify.
    const lead = flat.slice(Math.max(0, m.index - 400), m.index);
    if (!_PRIOR_KNOWLEDGE.test(lead)) return null;
    const incep = /\binception\s+date\s*:?\s*([0-9]{1,2}[\/\-][0-9]{1,2}[\/\-][0-9]{2,4})/i.exec(flat);
    return {
      label: 'Prior Knowledge condition',
      value: incep ? normalizeDate(incep[1]) : null,
      quote: flat.slice(m.index - 120 < 0 ? 0 : m.index - 120, m.index + 120).trim()
    };
  }

  /* A RETROACTIVE DATE STATED PER LOCATION.
   *
   * Ohio Eastern Star's Hanover package prints one retroactive date for
   * Employee Benefits Liability, 09/20/2003, on the declarations and in the
   * endorsement schedule. Then form 970-0015 replaces it:
   *
   *   EMPLOYEE BENEFITS LIABILITY COVERAGE – RETROACTIVE DATE (S) BY LOCATION
   *   Ohio Eastern Star Health Care Center  1451 Gambier Road ...  07/20/2021
   *   Ohio Eastern Star Home  125 Eastern Star Rd ...              01/06/2025
   *   Cemetary Route 3 Wooster Road                                07/20/2021
   *   The Retroactive Date shown in the Schedule of the Employee Benefits
   *   Liability Coverage (Claims-Made) Endorsement ... is replaced with the
   *   Retroactive Date shown in the Schedule above for the particular Location
   *
   * Abuse or Molestation (970-0014) does the same, and the Schedule of Insured
   * Locations (970-0012) does it for Professional and General Liability in
   * two columns. One date per coverage cannot carry this: the Home's
   * 01/06/2025 vanished from every row.
   *
   * Returns [{ coverage, rows: [{ location, date }] }]. Reads one printed row
   * per line, which is what spTableLines makes of both extracted text and a
   * LlamaParse markdown or HTML table.
   */
  const _BY_LOCATION = /\bretroactive\s+dates?\s*\(\s*s\s*\)\s*by\s+location\b|\bretroactive\s+dates?\s+by\s+location\b/i;
  const _LOCATION_SCHEDULE = /\bschedule\s+of\s+insured\s+locations\b/i;
  const _SCHED_END = /\ball\s+other\s+(?:policy\s+)?terms\s+and\s+conditions\s+remain\s+unchanged\b/i;
  const _FULL_DATE = /\b(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/((?:19|20)\d{2})\b/g;
  const _FORM_ROW = /^\s*\d{3,4}-\d{3,4}[A-Z]{0,2}\s+\d{2}\/\d{2}\b/;   // a forms-schedule entry, not a heading

  function scheduleRows(lines, from, coverages) {
    const rows = [];
    for (let j = from; j < lines.length && j < from + 80; j++) {
      const l = lines[j];
      if (_SCHED_END.test(l)) break;
      if (j > from && (_BY_LOCATION.test(l) || _LOCATION_SCHEDULE.test(l))) break;
      if (/\b(?:effective|expiration|issued)\s+date\b/i.test(l)) continue;
      const dates = [...l.matchAll(_FULL_DATE)];
      if (!dates.length || dates.length !== coverages.length) continue;
      // The dates end the row; anything after the last one is not this table.
      const tail = l.slice(dates[dates.length - 1].index + dates[dates.length - 1][0].length);
      if (/[A-Za-z]{3,}/.test(tail)) continue;
      const location = l.slice(0, dates[0].index).replace(/\s+/g, ' ').replace(/[\s|:,]+$/, '').trim();
      if (!/[A-Za-z]/.test(location)) continue;
      dates.forEach((d, k) => rows.push({ coverage: coverages[k], location: location.slice(0, 80), date: normalizeDate(d[0]) }));
    }
    return rows;
  }

  function readRetroByLocation(text) {
    const lines = spTableLines(text).split('\n');
    const out = [];
    const add = (coverage, rows) => {
      const mine = rows.filter(r => r.coverage === coverage).map(r => ({ location: r.location, date: r.date }));
      if (!mine.length) return;
      const prev = out.find(o => o.coverage.toLowerCase() === coverage.toLowerCase());
      if (prev) mine.forEach(r => { if (!prev.rows.some(x => x.location === r.location && x.date === r.date)) prev.rows.push(r); });
      else out.push({ coverage, rows: mine });
    };
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (_FORM_ROW.test(l)) continue;
      if (_BY_LOCATION.test(l)) {
        // The coverage is named ahead of "– Retroactive Date(s) by Location",
        // on this line or wrapped onto the one above.
        const head = ((lines[i - 1] || '') + ' ' + l).replace(/\s+/g, ' ');
        const m = /([A-Za-z][A-Za-z&' ]{3,80}?)\s+coverage\s*[-–—]\s*retroactive\s+dates?/i.exec(head);
        if (!m) continue;
        const coverage = m[1].replace(/\s+/g, ' ').trim();
        add(coverage, scheduleRows(lines, i + 1, [coverage]));
      } else if (_LOCATION_SCHEDULE.test(l)) {
        // One retroactive-date column per coverage part, named in the header
        // ahead of the first dated row, in the order the columns print.
        const span = lines.slice(i, i + 40);
        const firstRow = span.findIndex(x => [...x.matchAll(_FULL_DATE)].length > 0 && !/\b(?:effective|issued)\s+date\b/i.test(x));
        if (firstRow < 0) continue;
        const header = span.slice(0, firstRow).join(' ').replace(/\s+/g, ' ');
        if (!/retroactive/i.test(header)) continue;
        const named = [];
        const re = /\b(commercial\s+general\s+liability|general\s+liability|professional\s+liability|employee\s+benefits\s+liability|abuse\s+or\s+molestation\s+liability)\b/gi;
        let c;
        while ((c = re.exec(header)) !== null) {
          const name = c[1].replace(/\s+/g, ' ');
          if (!named.some(n => n.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(n.toLowerCase()))) named.push(name);
        }
        if (!named.length) continue;
        const rows = scheduleRows(lines, i + 1, named);
        named.forEach(n => add(n, rows));
      }
    }
    return out;
  }

  /* A RETROACTIVE DATE STATED PER NAMED INSURED.
   *
   * Ohio Eastern Star's Hanover package carries a Named Insured Schedule
   * (970-0016) with its own Retroactive Date column:
   *
   *   | Named Insured                                | Effective | Retroactive | Termination |
   *   | The Ohio Eastern Star Health Care Center Inc |           | 07/20/2019  |             |
   *   | Pine Hill Independent Senior Living          |           | 01/06/2025  |             |
   *
   * and paragraph B applies it to every claims-made part: "coverage applies
   * only to those Incidents ... on or after the Retroactive Date shown in the
   * Schedule for that Named Insured". Section 5C showed none of it.
   *
   * Read from a table grid, because the empty Effective and Termination cells
   * are what place the date in the Retroactive column. Flattened text cannot
   * tell which column a lone date sat in, so it is not guessed at.
   * Returns [{ namedInsured, date }].
   */
  function readRetroByNamedInsured(text) {
    const out = [];
    spTableGrid(text).forEach(g => {
      const head = (g.header || []).map(h => String(h || ''));
      const ni = head.findIndex(h => /named\s+insured/i.test(h));
      const rd = head.findIndex(h => /retroactive/i.test(h));
      if (ni < 0 || rd < 0) return;
      (g.rows || []).forEach(r => {
        const name = String(r[ni] || '').replace(/\s+/g, ' ').trim();
        const date = normalizeDate(String(r[rd] || ''));
        if (name && date && !out.some(o => o.namedInsured === name && o.date === date)) out.push({ namedInsured: name, date });
      });
    });
    return out;
  }

  /* Every "Continuity Date: <date>" with the part that states it. Read on its
   * own because a canonical "Retroactive Date" in the same document demotes
   * the continuity date out of the anchor list — right for choosing the
   * anchor, but Cincinnati's D&O and EPL declarations state "Retroactive Date:
   * N/A" beside "Continuity Date: 11-16-2005", and the second is still a term
   * the broker needs to see. */
  function readContinuityDates(text) {
    const src = spTableLines(text);
    const out = [];
    const re = /\bcontinuity\s+date\b/gi;
    let m;
    while ((m = re.exec(src)) !== null) {
      const after = src.slice(m.index + m[0].length, m.index + m[0].length + 60);
      if (!/^\s*[:|\-\u2013]?\s*[*_<]*\s*(?:u>)?\s*\d/.test(after.replace(/<\/?u>/g, ''))) continue;
      const d = findDate(after);
      if (!d) continue;
      out.push({ value: d.raw, normalized: d.normalized, index: m.index, coverage: coverageAt(src, m.index) || null });
    }
    return out;
  }

  /* HOW LONG A CRIME FORM GIVES TO DISCOVER A LOSS.
   *
   * A loss-sustained or discovery form has no ERP; the question 5C's tail
   * column asks becomes "how long after the policy ends can a loss still be
   * discovered?" — and the form answers it. Philadelphia's PI-ULT-009:
   *
   *   3. Discovery Period for Loss — We will pay only for covered loss
   *      discovered no later than one year from the end of the policy period.
   *
   * 5C printed "verify term" for a term stated in the form. Returns
   * { term, basis, quote } or null. */
  const _NUM_WORDS = { one: 1, two: 2, three: 3, four: 4, six: 6, twelve: 12, sixty: 60, ninety: 90 };
  function readDiscoveryPeriod(text) {
    const src = spTableLines(text).replace(/\s+/g, ' ');
    const unit = (n, u) => {
      const k = /^\d+$/.test(n) ? parseInt(n, 10) : _NUM_WORDS[String(n).toLowerCase()];
      if (!k) return null;
      const base = u.toLowerCase().replace(/s$/, '');
      return k + ' ' + base + (k === 1 ? '' : 's');
    };
    const forms = [
      { re: /\bdiscovered\s+no\s+later\s+than\s+(\d+|one|two|three|sixty|ninety|twelve)\s+(years?|months?|days?)\s+(?:from|after)\s+the\s+end\s+of\s+the\s+policy\s+period\b/i,
        basis: 'after the policy period ends' },
      { re: /\bextended\s+period\s+to\s+discover\s+loss\b[\s\S]{0,400}?\b(\d+|one|two|three|sixty|ninety|twelve)\s+(years?|months?|days?)\s+after\b/i,
        basis: 'after the policy is cancelled or ends' }
    ];
    for (const f of forms) {
      const m = f.re.exec(src);
      if (!m) continue;
      const term = unit(m[1], m[2]);
      if (term) return { term, basis: f.basis, quote: m[0].trim().slice(0, 200) };
    }
    /* The ISO discovery form (CR 00 20), as Aspen View's GuideOne package
     * prints it:
     *   Extended Period To Discover Loss — We will pay for loss that you
     *   sustained prior to the effective date of cancellation … which is
     *   "discovered" by you: (1) No later than 60 days from the date of that
     *   cancellation… (2) No later than one year from the date of that
     *   cancellation with regard to any "employee benefit plan".
     * The plan term is reported alongside the main one. */
    const ep = /\bextended\s+period\s+to\s+discover\s+loss\b[\s\S]{0,400}?\bno\s+later\s+than\s+(\d+|one|two|three|sixty|ninety|twelve)\s+(years?|months?|days?)\s+from\s+the\s+date\s+of\s+(?:that\s+)?cancellation/i.exec(src);
    if (ep) {
      const term = unit(ep[1], ep[2]);
      const plan = /\bno\s+later\s+than\s+(\d+|one|two|three|twelve)\s+(years?|months?)\s+from\s+the\s+date\s+of\s+(?:that\s+)?cancellation\s+with\s+regard\s+to\s+any\s+["\u201c]?employee\s+benefit\s+plan/i
        .exec(src.slice(ep.index, ep.index + 1500));
      if (term) return { term, basis: 'after cancellation', planTerm: plan ? unit(plan[1], plan[2]) : null, quote: ep[0].trim().slice(0, 200) };
    }
    return null;
  }

  /* RETROACTIVE DATES FOR THE CLAIMS-MADE PARTS OF AN OCCURRENCE POLICY.
   *
   * Aspen View's GuideOne umbrella is occurrence (CU 00 01) but carries
   * claims-made umbrella coverage for four parts, each with its own date:
   *   RETROACTIVE DATE (FOR CLAIMS-MADE COVERAGE ONLY)
   *   Coverage EB : Employee Benefits Liability      07/01/2014
   *   Coverage EL : Educators Legal Liability        08/01/2012
   *   …
   * 5C reported the umbrella as "N/A — occurrence form" and never showed
   * them, though EL/DO/EP sit a month after the underlying 07/01/2012.
   * Returns [{ coverage, date }]. */
  function readSubCoverageRetro(text) {
    const lines = spTableLines(text).split('\n');
    const out = [];
    for (let i = 0; i < lines.length; i++) {
      if (!/retroactive\s+dates?\s*\(\s*for\s+claims[\s\-]*made\s+coverages?\s+only\s*\)/i.test(lines[i])) continue;
      for (let j = i + 1; j < lines.length && j < i + 25; j++) {
        const l = lines[j].replace(/\s+/g, ' ').trim();
        if (!l || /^coverage\s+retroactive\s+date$/i.test(l)) continue;
        const m = /^(?:coverage\s+[A-Z]{1,3}\s*:\s*)?([A-Za-z][A-Za-z&'\/ ]{3,80}?)\s+((?:0?[1-9]|1[0-2])\/(?:0?[1-9]|[12]\d|3[01])\/(?:19|20)\d{2})$/i.exec(l);
        if (!m) { if (out.length) break; else continue; }
        out.push({ coverage: m[1].trim(), date: normalizeDate(m[2]) });
      }
      if (out.length) break;
    }
    return out;
  }

  /* A CONTINUITY DATE THAT IS ONLY A KNOWLEDGE CUT-OFF.
   *
   * "Continuity Date" is read as the prior-acts anchor because on a Beazley
   * declarations it is one. On Ohio Eastern Star's Elpha cyber policy it is
   * not. Its declarations print "Continuity Date: 20 Jul 2025", and the only
   * place the wording uses that date is a prior-knowledge exclusion:
   *
   *   3. act, error, omission, incident, or event committed or occurring
   *      prior to the Continuity Date ..., if any member of the Control Group
   *      knew or could have reasonably foreseen before the Continuity Date ...
   *      that such act ... might be expected to be the basis of a Claim
   *
   * Earlier acts are covered unless someone knew about them. A retroactive
   * date cuts them off whether anyone knew or not. Comparing that date with
   * the package's real retroactive date 07/20/2021 raised a "Retro Date
   * Misalignment" banner between two dates that measure different things.
   *
   * True only when the wording uses the date AND every use sits next to
   * knowledge language. A form that uses its Continuity Date as a plain cut-off
   * anywhere is read as a retro date, as before.
   */
  const _KNOWLEDGE_WORDS = /\b(?:knew|known|know|knowledge|aware(?:ness)?|foresee(?:n|able)?|reasonably\s+(?:have\s+)?(?:expected|anticipated))\b/i;

  function readContinuityKnowledgeOnly(text) {
    const flat = spTableLines(text).replace(/-\s*\n\s*/g, '').replace(/\s+/g, ' ');
    const re = /\bcontinuity\s+date\b/gi;
    let m, uses = 0, quote = null;
    while ((m = re.exec(flat)) !== null) {
      // The declarations field itself ("Continuity Date: 20 Jul 2025") is the
      // value being judged, not a use of it.
      const after = flat.slice(m.index + m[0].length, m.index + m[0].length + 40);
      // Emphasis around the value ("| Continuity Date: | *11-16-2005* |") is
      // still the field, not a use of it.
      if (/^\s*[:\-\u2013]?\s*["\u201c*_]*\s*(?:\d|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b)/i.test(after)) continue;
      uses++;
      const around = flat.slice(Math.max(0, m.index - 400), m.index + 300);
      if (!_KNOWLEDGE_WORDS.test(around)) return null;
      if (!quote) quote = flat.slice(Math.max(0, m.index - 160), m.index + 200).trim();
    }
    return uses ? { quote } : null;
  }

/* THE TRIGGER A COVERAGE PART STATES IN ITS OWN TERMS.
 *
 * Until now a trigger came from three places: a declarations notice, the
 * forms schedule, or prose weighed near a heading. A crime part on a
 * portfolio policy has none of those — the notice governs the LIABILITY
 * parts and says so, and the form is carrier-proprietary with no trigger in
 * its title — so Crime, Kidnap Ransom & Extortion and Workplace Violence all
 * reported an unread trigger, and their prior-acts cells then had to report
 * that the applicability of a retroactive date was unknown too. One gap, six
 * uninformative cells.
 *
 * The part states it plainly in its insuring clause:
 *
 *   the Company agrees to pay for loss any Insured shall sustain at any time
 *   prior to the termination of this Coverage Part which is Discovered
 *   during the Policy Period
 *
 *   XII. DISCOVERY PERIOD  ... this Coverage Part applies only to loss first
 *   Discovered by the Insured: (A) during the Policy Period
 *
 * That is a discovery form, asserted by the contract about itself, and it is
 * the strongest evidence available — better than a form number, because it
 * does not depend on recognising the form.
 *
 * The patterns are deliberately narrow. "Discovered" appears 53 times in this
 * policy, nearly all of it valuation and notice machinery ("the business day
 * on which a loss is Discovered"). Only a clause that makes discovery the
 * CONDITION OF COVERAGE counts, which is why each pattern requires the
 * coverage-granting words around it.
 */
  /* Each pattern must express the CONDITION OF COVERAGE for the whole part,
   * not a grant inside it. A first draft allowed "pay for loss ... discovered
   * ... during the policy period", which matched a cyber policy's Utility
   * Fraud sublimit — "pay for Loss up to the Utility Fraud Limit ... provided
   * the Loss first occurs and is discovered" — and would have relabelled a
   * claims-made cyber policy as a discovery form off one sublimit. */
  const TERMS_TRIGGER = [
    { trigger: 'Discovery',
      re: /\bwhich\s+is\s+discovered\s+during\s+the\s+policy\s+period\b/i },
    { trigger: 'Discovery',
      re: /\bapplies\s+only\s+to\s+loss\s+first\s+discovered\b/i },
    { trigger: 'Discovery',
      re: /\bapplies\s+only\s+to\s+loss\s+discovered\s+during\s+the\s+policy\s+period\b/i },
    { trigger: 'Loss Sustained',
      re: /\bapplies\s+only\s+to\s+loss\s+sustained\s+during\s+the\s+policy\s+period\b/i },
    /* The ISO crime loss-sustained wording, as Philadelphia's Crime Coverage
     * Form (PI-ULT-009) prints it in its Policy Period condition:
     *
     *   Subject to the Loss Sustained During Prior Insurance condition, we
     *   will pay only for loss that you sustain through acts committed or
     *   events occurring during the Policy Period.
     *
     * Ohio Eastern Star's 5C read that part as Occurrence. The clause sits
     * some 25,000 characters below the form's heading, past any lookback,
     * so it names its coverage itself: this wording is crime-form wording. */
    { trigger: 'Loss Sustained', hint: 'Crime',
      re: /\bwe\s+will\s+pay\s+only\s+for\s+loss\s+that\s+you\s+sustain\s+through\s+acts\s+committed\s+or\s+events\s+occurring\s+during\s+the\s+policy\s+period\b/i },
    /* An occurrence trigger stated in the part's own terms. The AmWins /
     * Everspan liquor form (L-4097S): "This insurance applies only to injury
     * which occurs during the policy period". Attributed by heading, like the
     * rest — no hint, because older liability forms use similar words. */
    { trigger: 'Occurrence',
      re: /\bthis\s+insurance\s+applies\s+only\s+to\s+injury\s+which\s+occurs\s+during\s+the\s+policy\s+period\b/i }
  ];

  function readTermsTriggers(text) {
    const src = spTableLines(text);
    if (!src) return [];
    const out = [];
    const seen = new Set();
    for (const def of TERMS_TRIGGER) {
      // Matched against the source, not a flattened copy, so the offset is
      // real and coverageAt can attribute the clause to the part that states
      // it. Flattening first cost exactly that: the crime part's own insuring
      // clause was found and then attributed to nothing.
      const re = new RegExp(def.re.source.replace(/\\s\+/g, '\\s+'), 'gi');
      let m;
      while ((m = re.exec(src)) !== null) {
        const cov = coverageAt(src, m.index) || def.hint || null;
        const key = (cov || '') + '::' + def.trigger;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ trigger: def.trigger, coverage: cov || null, quote: m[0].trim().slice(0, 200) });
        if (re.lastIndex <= m.index) re.lastIndex = m.index + 1;
      }
    }
    return out;
  }

  function readPriorProceedings(text) {
    const lines = spTableLines(text);
    if (!lines) return [];
    const arr = lines.split('\n');
    const out = [];
    let at = 0;
    for (let i = 0; i < arr.length; i++) {
      const line = arr[i];
      const lineAt = at;
      at += line.length + 1;
      if (!PRIOR_PROCEEDINGS.some(re => re.test(line))) continue;
      const tail = line.replace(/^[\s\S]*?(?:proceedings?|litigation|claims?)\s*(?:date)?\s*[:\-\u2013]?/i, '');
      const val = findDate(tail) || findDate(arr[i + 1] || '');
      if (val) {
        out.push({
          // findDate returns `raw`, not `value` — the same slip the ERP reader
          // made once. Reading a field name that does not exist yields
          // undefined, which prints as a blank date beside a confident label.
          value: val.raw,
          normalized: val.normalized,
          // Strip only a declarations item marker — "(E) ", "4. ", "Item 5 " —
          // and nothing else. A character class of [\s(A-Z)] eats the first
          // letter of the label itself: "(E)    Pending" became "ending".
          label: line.replace(/\s*[:\-\u2013][\s\S]*$/, '')
                     .replace(/^\s*(?:\([A-Za-z0-9]{1,3}\)|[A-Za-z0-9]{1,3}[.)]|item\s+\d+[.)]?)\s*/i, '')
                     .trim().slice(0, 60),
          /* WHOSE DATE THIS IS.
           *
           * A portfolio declares one of these per coverage part, and they do
           * not agree: the Chubb ForeFront reviewed here states May 1 2005 for
           * Directors & Officers, May 1 2005 for Employment Practices and
           * May 1 **2008** for Fiduciary. Handing every part the first one
           * found — which is what taking [0] did — would print 2005 against
           * Fiduciary: a real date, from the wrong coverage, which is the
           * exact failure mode this engine keeps having to design out. */
          index: lineAt,
          coverage: coverageAt(lines, lineAt) || null
        });
      }
    }
    return out;
  }

  /* A CROSS-REFERENCE IS AN ANSWER, NOT A SILENCE.
   *
   * Tulsa Honor Academy's excess policy, Item 5: "See: Underlying Claims-Made
   * Coverage Endorsement". The declarations were asked for a retro date and
   * they answered — by pointing somewhere else. "Not found" says the question
   * went unanswered, which sends a broker looking in the wrong document.
   */
  const RETRO_POINTER = /\b(?:see|refer\s+to|as\s+(?:per|stated\s+in)|per)\b[^.\n]{0,60}?\b(?:endorsement|schedule|underlying|attached|form|policy)\b/i;

  /** A retro label whose value is a pointer rather than a date, or null. */
  function readRetroPointer(text) {
    const lines = spTableLines(text);
    if (!lines) return null;
    const arr = lines.split('\n');
    for (let i = 0; i < arr.length; i++) {
      const line = arr[i];
      if (!RETRO_LABELS.some(l => l.re.test(line))) continue;
      if (NOT_RETRO.some(re => re.test(line))) continue;
      if (findDate(line)) continue;                       // it answered with a date
      const tail = line.replace(/^[\s\S]*?date\s*[:\-\u2013]?/i, '').trim();
      const probe = tail || (arr[i + 1] || '').trim();
      if (probe && RETRO_POINTER.test(probe) && !findDate(probe)) {
        return { pointer: probe.replace(/\s+/g, ' ').trim().slice(0, 90),
                 label: line.replace(/\s*[:\-\u2013][\s\S]*$/, '').trim().slice(0, 60) };
      }
    }
    return null;
  }

  /* PROPERTY STATED AS A SCHEDULE OF LOCATIONS.
   *
   * A third layout, and the one Matrix Human Services uses. There is no
   * Building/BPP grid to read: there are sixteen location blocks, each
   * repeating the same field names, with the coverage NAME in the middle
   * column and the amount at the end of the same line.
   *
   *     Location Number 0005
   *     13560 E McNichols Rd
   *     Building
   *     Number:  001        Matrix Community Center       Limit Of Insurance
   *       Coverage          Building                            $7,000,000
   *     Occupancy
   *       Coverage          All Personal Property               $1,000,000
   *
   * readPairedTableValues returns nothing here, because the label column says
   * "Coverage" on every row and the thing being labelled is in the value. The
   * report therefore said the property limits could not be parsed on a policy
   * that states them sixteen times over.
   *
   * Fourteen of those locations read "Contents Only" with no amount, which is
   * itself a finding — a leased site with no building limit — and is returned
   * as a count rather than silently dropped.
   */
  const SCHED_ROW = /^\s*Coverage\s{2,}([A-Za-z][A-Za-z \-\/&]{2,40}?)\s{2,}\$\s*([\d][\d,]{2,})\s*$/;
  const SCHED_BARE = /^\s*Coverage\s{2,}(Contents\s+Only|Building)\s*$/i;
  const SCHED_BI = /^\s*(Business\s+Income|Time\s+Element|Extra\s+Expense)\b[^$\n]{0,40}\$\s*([\d][\d,]{2,})\s*$/i;

  function _schedClass(name) {
    const n = String(name || '').toLowerCase().trim();
    if (/^building/.test(n)) return 'building';
    if (/personal\s+property|^contents\b|^stock\b/.test(n)) return 'bpp';
    if (/business\s+income|time\s+element|extra\s+expense|rental\s+value/.test(n)) return 'businessIncome';
    return null;
  }

  /** Property limits stated per location, summed by class.
   *
   *  Returns null when the document is not of this shape, so a caller can fall
   *  through to the grid reader without having to know which layout it has. */
  function readScheduledProperty(text) {
    const lines = spTableLines(text);
    if (!lines) return null;
    const arr = lines.split('\n');
    const out = { building: 0, bpp: 0, businessIncome: 0,
                  counts: { building: 0, bpp: 0, businessIncome: 0 },
                  contentsOnly: 0, locations: 0, rows: [] };
    const seenLoc = new Set();
    for (const line of arr) {
      const loc = /\bLocation\s+Number\s+(\d+)/i.exec(line);
      if (loc) { seenLoc.add(loc[1]); continue; }
      if (SCHED_BARE.test(line)) {
        if (/contents\s+only/i.test(line)) out.contentsOnly++;
        continue;
      }
      let m = SCHED_ROW.exec(line);
      let name, amount;
      if (m) { name = m[1]; amount = m[2]; }
      else {
        m = SCHED_BI.exec(line);
        if (!m) continue;
        name = m[1]; amount = m[2];
      }
      const cls = _schedClass(name);
      if (!cls) continue;
      const n = Number(String(amount).replace(/,/g, ''));
      // The same floor the rest of the engine uses for a limit: a multiple of
      // 500 and at least 1,000, so a premium or a policy number cannot become
      // a building limit.
      if (!isFinite(n) || n < 1000 || n % 500 !== 0) continue;
      out[cls] += n;
      out.counts[cls]++;
      out.rows.push({ cls, name: String(name).trim(), amount: n });
    }
    out.locations = seenLoc.size;
    /* A SCHEDULE OF LOCATIONS HAS LOCATIONS.
     *
     * Without this the reader fired on an ordinary two-line declarations page
     * — "COMMERCIAL PROPERTY DECLARATIONS / Building $5,000,000 / Business
     * Income $1,000,000" — matched the Business Income line, and returned a
     * "schedule" of nothing, which the caller then wrote over the real
     * extraction. A reader for a rarer layout must be HARDER to satisfy than
     * the common one it sits beside, not easier.
     *
     * Two, not one: a single location is a declarations page with an address
     * on it, and the grid reader already handles that shape better than this
     * one would.
     */
    if (out.locations < 2 || out.rows.length < 2) return null;
    return out;
  }

  root.spRetroAnchor = {
    readRetroAnchors, readAttributedAnchors, coverageAt, readErp, normalizeDate, findDate,
    readFormTriggers, readFormsSchedule, formAt, ISO_FORM_TRIGGER, inGenericNotice,
    spTableLines, readPairedTableValues, cellItems,
    COVERAGE_BLOCK,
    RETRO_LABELS, NOT_RETRO, ERP_LABELS, NOT_ERP, DATE_FORMS,
    readPriorProceedings, readPriorKnowledge, readContinuityKnowledgeOnly, readContinuityDates, readDiscoveryPeriod, readSubCoverageRetro, readRetroByLocation, readRetroByNamedInsured, readRetroPointer, readScheduledProperty,
    readTermsTriggers, spTableGrid
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = root.spRetroAnchor;
})();
